// Data seam for concierge messaging (the in-platform two-way thread between a
// client and their INHERTA account manager). Client-side surface: the tenant
// owner/admin talks to the AM assigned to their tenant.
//
// Wired to the live endpoints:
// - getConciergeSummary() -> GET /concierge/summary  (unread + manager, for the nav badge)
// - getConciergeThread()  -> GET /concierge          (full thread; marks manager msgs read)
// - sendConciergeMessage() -> POST /concierge/messages
//
// The AM-side seam lives in ../admin/admin-concierge-data.ts and reuses the
// ConciergeMessage / ConciergeManager types re-exported from here.

import { apiClient } from '../../api/client.js'

/** One message in a concierge thread. `side` says who wrote it. */
export interface ConciergeMessage {
  id: string
  side: 'manager' | 'client'
  authorName: string
  body: string
  createdAt: string
  read: boolean
}

/** The account manager assigned to the tenant. */
export interface ConciergeManager {
  id: string
  name: string
  email: string
}

export interface ConciergeSummary {
  unread: number
  manager: ConciergeManager | null
}

export interface ConciergeThreadData {
  messages: ConciergeMessage[]
  manager: ConciergeManager | null
}

/** Unread count + the assigned manager. Drives the nav badge; cheap to poll. */
export async function getConciergeSummary(): Promise<ConciergeSummary> {
  const res = await apiClient.get<{ data: ConciergeSummary }>('/concierge/summary')
  return res.data.data
}

/** Full thread with the manager. The GET marks the manager's messages read. */
export async function getConciergeThread(): Promise<ConciergeThreadData> {
  const res = await apiClient.get<{ data: ConciergeThreadData }>('/concierge')
  return res.data.data
}

/** Send a message to the manager. Returns the persisted message. */
export async function sendConciergeMessage(body: string): Promise<ConciergeMessage> {
  const res = await apiClient.post<{ data: { message: ConciergeMessage } }>('/concierge/messages', {
    body,
  })
  return res.data.data.message
}

/** Two-letter initials for an avatar, from a display name. */
export function conciergeInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || '?'
}

/** Format a message timestamp for the thread: time-of-day today, else date + time. */
export function formatConciergeTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
  const sameDay = d.toDateString() === new Date().toDateString()
  if (sameDay) return time
  const date = d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })
  return `${date}, ${time}`
}
